import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { borrowerService } from "@/services/borrowers";

/**
 * Re-run UniCourt search (non-destructive) and refetch cases for Default History.
 */
export function useRefreshUnicourtCasesMutation(borrowerId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (borrowerId == null) throw new Error("No borrower ID");
      await borrowerService.refreshUnicourtCases(borrowerId);
      await queryClient.refetchQueries({ queryKey: queryKeys.unicourtCases(borrowerId) });
    },
  });
}

/**
 * Refresh DataMerch notes from upstream and refetch.
 */
export function useRefreshDatamerchNotesMutation(borrowerId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (borrowerId == null) throw new Error("No borrower ID");
      await borrowerService.refreshDatamerch(borrowerId);
      await queryClient.refetchQueries({ queryKey: queryKeys.datamerchNotes(borrowerId) });
    },
  });
}

/**
 * Refresh one UniCourt case row.
 */
export function useRefreshUnicourtCaseMutation(borrowerId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      if (borrowerId == null) throw new Error("No borrower ID");
      await borrowerService.refreshUnicourtCase(borrowerId, caseId);
    },
    onSettled: () => {
      if (borrowerId != null) {
        queryClient.invalidateQueries({ queryKey: queryKeys.unicourtCases(borrowerId) });
      }
    },
  });
}

/**
 * Re-run statement AI analysis (AI Analysis tab).
 * Prefer `useRerunStatementAnalysisMutation` when the caller also needs
 * analysis-run list invalidation.
 */
export function useRerunAnalysisMutation(borrowerId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (borrowerId == null) throw new Error("No borrower ID");
      return borrowerService.rerunAnalysis(borrowerId, undefined, false);
    },
    onSuccess: () => {
      if (borrowerId != null) {
        queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
        queryClient.invalidateQueries({
          queryKey: ["borrowerTransactions", Number(borrowerId)] as const,
        });
      }
    },
  });
}
